import { User } from "../../../models/Person";
import { IntakeAnswer, IntakeRole } from "../types/interviewSession";
import { getAverageFaceEmotions } from "./faceEmotionAccumulator";
import {
  buildProfileKeywords,
  mapOccupationToJourney,
} from "./intakeProfile";
import { MatchScoringInput } from "./matchScoring";
import { scoreAnswersWithRubrics } from "./rubricScoring";

export type SessionProfileContext = {
  occupationStatus?: string | null;
  isSurveyForm?: boolean;
  profile?: User | null;
};

function inferJourneyFallback(answers: IntakeAnswer[]): string {
  const trackAnswer = answers.find((a) => a.trackPreference);
  if (trackAnswer?.trackPreference === "business") return "entrepreneur";
  if (trackAnswer?.trackPreference === "personal") return "student";

  const q1Text =
    answers.find((a) => a.stepId === "q1" && !a.skipped)?.transcript.toLowerCase() ??
    "";

  if (/entrepreneur|startup|founder|venture|building something/.test(q1Text)) {
    return "entrepreneur";
  }
  if (/corporate|director|vp|manager|organization|leader/.test(q1Text)) {
    return "corporate";
  }
  if (/student|early career|finding your career/.test(q1Text)) {
    return "student";
  }

  return "corporate";
}

export function resolveJourneyType(
  answers: IntakeAnswer[],
  profileContext?: SessionProfileContext
): string {
  if (profileContext?.occupationStatus) {
    return mapOccupationToJourney(profileContext.occupationStatus);
  }
  return inferJourneyFallback(answers);
}

export function buildMatchScoringInput(
  answers: IntakeAnswer[],
  role: IntakeRole,
  profileContext?: SessionProfileContext
): MatchScoringInput {
  const answered = answers.filter((a) => !a.skipped && a.transcript.trim());
  const sentiments = answered.map((a) => a.sentiment);
  const rubrics = scoreAnswersWithRubrics(answers);

  const sentimentScore =
    sentiments.length > 0
      ? sentiments.reduce((sum, s) => sum + s.score, 0) / sentiments.length
      : 0;

  const sentimentMagnitude =
    sentiments.length > 0
      ? sentiments.reduce((sum, s) => sum + s.magnitude, 0) / sentiments.length
      : 0;

  const face = getAverageFaceEmotions();
  const profileKeywords = buildProfileKeywords(
    profileContext?.profile ?? null,
    profileContext?.isSurveyForm ?? false
  );

  const interviewTranscripts = answered.map((a) => a.transcript);

  return {
    sentimentScore,
    sentimentMagnitude,
    faceJoy: face.joy,
    faceSorrow: face.sorrow,
    faceAnger: face.anger,
    transcripts: [...interviewTranscripts, ...profileKeywords],
    journeyType: resolveJourneyType(answers, profileContext),
    role,
    allAnswerMagnitudes: sentiments.map((s) => s.magnitude),
    rubricAccountability: rubrics.accountability,
    rubricCoachability: rubrics.coachability,
    rubricFlags: rubrics.flags,
  };
}

export function userToScoringMentor(user: User) {
  const name = [user.name, user.last_name].filter(Boolean).join(" ").trim();
  return {
    id: String(user.id),
    name: name || `Mentor ${user.id}`,
    title: user.advisor_title || user.shortDescription || "Mentor",
    skills: [...(user.categories ?? []), ...(user.industries ?? [])],
    industry:
      user.current_industry ||
      user.industries?.[0] ||
      user.sub_industry ||
      "General",
    bio: [user.fullBio, user.shortDescription, user.advisor_company]
      .filter(Boolean)
      .join(" "),
  };
}
